'use strict'

import {MASTER_LOT_STATUSES} from '../constants'

/**
 * Source-unit allocation map + decoration (picker collision guard).
 *
 * Answers: "which master lot already owns this source unit?"
 *
 * In v1 the listing endpoint will emit `allocatedMasterLotId` per unit row
 * out of a `LEFT JOIN master_lot_components` on the new `master_lots` table;
 * in the prototype that field doesn't exist on the backend yet, so this
 * module synthesizes it from the persisted `state.byId` slice at
 * vuex ingress. When the BE field lands, both helpers below become no-ops
 * or delete outright — neither the picker nor its consumers see a
 * contract change.
 *
 * Both helpers are pure: no vuex, no Vue, no in-place mutation. The vuex
 * cache stays the BE-shaped payload; decoration produces a fresh array of
 * fresh unit objects so two consumers reading the same cached lots can't
 * stomp on each other's allocation view.
 */

const PARSABLE_TIMESTAMP_FALLBACK = Number.POSITIVE_INFINITY

/**
 * Walk every non-cancelled master lot in the input and produce a map from
 * picker `unit.id` to the owning lot's `masterLotCode`. Manual entries are
 * ignored — their synthesized ids never overlap with picker unit ids by
 * construction.
 *
 * Multi-claim ambiguity (the same `unit.id` claimed by two non-cancelled
 * lots — only possible today because the mock-api write boundary doesn't
 * enforce uniqueness yet, but inevitable in any hand-edited fixture set)
 * resolves to the earliest owner by `Date.parse(createdAt)` ASC. A
 * `Number.isFinite` guard on the parsed value sends malformed or missing
 * timestamps to the end of the sort, matching the eventual DB unique
 * constraint's "first INSERT wins" semantics.
 *
 * Returns `{}` for empty / null input — safe to compose with `||` defaults.
 */
export function buildSourceUnitAllocationMap(masterLots) {
  if (!Array.isArray(masterLots) || masterLots.length === 0) return {}

  const sorted = [...masterLots]
    .filter((lot) => lot && lot.status !== MASTER_LOT_STATUSES.CANCELLED)
    .sort((a, b) => createdAtSortKey(a) - createdAtSortKey(b))

  const map = {}
  for (const lot of sorted) {
    const sourceUnits = lot?.manifest?.sourceUnits
    if (!Array.isArray(sourceUnits)) continue
    const owner = lot.masterLotCode || lot.id
    if (!owner) continue
    for (const unit of sourceUnits) {
      if (!unit || !unit.id) continue
      // First non-cancelled claim wins; subsequent claims are silently
      // dropped because the sort already put the earliest creator first.
      if (map[unit.id] === undefined) map[unit.id] = owner
    }
  }
  return map
}

/**
 * Return a new array of source lots where each unit has been shallow-cloned
 * with an `allocatedMasterLotId` field added. Precedence (forward-compat
 * with v1):
 *   1. unit.allocatedMasterLotId (BE-served, source of truth when present)
 *   2. allocationMap[unit.id]    (FE synthesis, prototype-only)
 *   3. null
 *
 * Once v1's `LEFT JOIN master_lot_components` lands the FE-synthesized map
 * becomes a no-op and (1) takes over without code change here. The cached
 * input array and its unit objects are never mutated.
 */
export function decorateSourceLotsWithAllocation(lots, allocationMap) {
  if (!Array.isArray(lots) || lots.length === 0) return []
  const map = allocationMap || {}
  return lots.map((lot) => ({
    ...lot,
    units: Array.isArray(lot?.units)
      ? lot.units.map((unit) => ({
          ...unit,
          allocatedMasterLotId: unit?.allocatedMasterLotId ?? map[unit?.id] ?? null,
        }))
      : [],
  }))
}

function createdAtSortKey(lot) {
  const parsed = Date.parse(lot?.createdAt)
  return Number.isFinite(parsed) ? parsed : PARSABLE_TIMESTAMP_FALLBACK
}
